import { useDependency } from 'ditox-react';
import { useTranslation } from 'react-i18next';
import { WEB_SOCKET_MANAGER_TOKEN } from '@/container';
import useGroupInfo from '@/hooks/groups/use-group-info';
import type { ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { Role } from '@/models/group/group-info';
import { logMessage } from '@/services/log-message';
import { useUserId } from '@/stores/auth';
import { useMessageStore } from '@/stores/message';
import { usePeriodicMessageStore } from '@/stores/periodic-message';
import { makePrompt } from '@/stores/prompter';
import { generateSessionKey } from '@/utils/session';

export default function useDeleteMessages(type: ConversationType, id: number) {
  const { t } = useTranslation();
  const userId = useUserId();
  const webSocketManager = useDependency(WEB_SOCKET_MANAGER_TOKEN);
  const { canManage, data } = useGroupInfo(type === 'group' ? id : undefined);

  const deleteMessage = (message: Pick<ConversationMessageTypeUnion, 'ID'>) => {
    if (!id) return;

    useMessageStore.getState().delete(message.ID, type);
    const sessionKey = generateSessionKey(type, id);
    usePeriodicMessageStore.getState().removeBurnableMessageId(sessionKey, message.ID);

    webSocketManager.dispatch({
      action: 'deleteMessage',
      params: {
        type,
        messageId: message.ID,
        sessionId: id,
      },
    });
  };

  const promptDeleteMessages = async (messages: ConversationMessageTypeUnion[]) => {
    const result = await makePrompt({
      key: 'delete-messages',
      type: 'dialog',
      title: t('conversation.delete.confirm-message'),
      resolutionButtons: [
        {
          label: t('conversation.delete.for-myself'),
          resolution: 'for-myself',
        },
        {
          label: t('conversation.delete.for-everyone'),
          resolution: 'for-everyone',
          hidden: type === 'group' && !canManage,
        },
      ],
    });

    const groupOwner = data?.Members?.find((m) => m.Role === Role.Owner)?.UserID;
    const groupManagers = data?.Members?.filter((m) => m.Role === Role.Admin).map((m) => m.UserID);
    const getCannotDeleteForEveryoneReason = getCannotDeleteForEveryoneReasonFactory(userId, groupOwner, groupManagers);

    switch (result) {
      case 'for-everyone':
        if (messages.some((m) => getCannotDeleteForEveryoneReason(m) !== null)) {
          makePrompt({
            key: 'delete-messages-failed',
            type: 'alert',
            title: t('conversation.delete.failed'),
            content: t('conversation.delete.failed-content'),
          });
          throw new Error('DELETE_FOR_EVERYONE_FAILED');
        }

        for (const message of messages) {
          deleteMessage(message);
        }
        break;
      case 'for-myself':
        logMessage('DELETE_MESSAGE', {
          messageIds: messages.map((m) => m.ID),
          sessionKey: generateSessionKey(type, id),
        });
        await Promise.allSettled(
          messages.map(async (message) => {
            useMessageStore.getState().delete(message, type);
          }),
        );
        break;
      default:
        break;
    }

    return result;
  };

  return {
    deleteMessage,
    promptDeleteMessages,
  };
}

const getCannotDeleteForEveryoneReasonFactory =
  (userId?: number, ownerId?: number, managerIds?: number[]) =>
  (message: ConversationMessageTypeUnion): string | null => {
    // 1. is within 7 days
    if (message.CreateTime <= Date.now() - 7 * 24 * 60 * 60 * 1000) return 'overdue';

    // 2. message type
    if ('ChatSessionID' in message && message.ChatSessionID) {
      if (!userId) return '';
      // msg is from myself
      if (message.UserID !== userId) return 'not-mine';
    } else if ('GroupID' in message && message.GroupID) {
      if (!ownerId || !userId) return '';
      // i am group owner
      if (userId === ownerId) return null;

      // msg is from owner
      if (message.UserID === ownerId) return 'from-owner';

      // i am not group manager
      if (!managerIds?.includes(userId)) return '';
    }

    return null;
  };
